import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import NieuwjaarsreceptieTab from '../components/NieuwjaarsreceptieTab';
import BibliotheekTab from '../components/BibliotheekTab';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle,
  AlertDialogTrigger
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Make BACKEND_URL available globally in this file for upload functions
window.BACKEND_URL = BACKEND_URL;

// Helper function to get full image URL
const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  return `${BACKEND_URL}${path}`;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [team, setTeam] = useState([]);
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [partners, setPartners] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [selectedAlbumPhotos, setSelectedAlbumPhotos] = useState([]);
  const [heroGallery, setHeroGallery] = useState([]);
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal states
  const [eventDialog, setEventDialog] = useState({ open: false, data: null });
  const [teamDialog, setTeamDialog] = useState({ open: false, data: null });
  const [partnerDialog, setPartnerDialog] = useState({ open: false, data: null });
  const [albumDialog, setAlbumDialog] = useState({ open: false, data: null });
  const [photoDialog, setPhotoDialog] = useState({ open: false, albumId: null });
  const [heroGalleryDialog, setHeroGalleryDialog] = useState({ open: false, data: null });
  
  // Delete confirmation states
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchData = async () => {
    try {
      const [eventsRes, teamRes, settingsRes, messagesRes, partnersRes, albumsRes, heroGalleryRes, newsletterRes, volunteersRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/team`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/contact`, getAuthHeaders()),
        axios.get(`${API}/partners`),
        axios.get(`${API}/albums`),
        axios.get(`${API}/hero-gallery`),
        axios.get(`${API}/newsletter/subscribers`, getAuthHeaders()),
        axios.get(`${API}/volunteers`, getAuthHeaders())
      ]);
      setEvents(eventsRes.data);
      setTeam(teamRes.data);
      setSettings(settingsRes.data);
      setMessages(messagesRes.data);
      setPartners(partnersRes.data);
      setAlbums(albumsRes.data);
      setHeroGallery(heroGalleryRes.data);
      setNewsletterSubscribers(newsletterRes.data);
      setVolunteers(volunteersRes.data);
      setIsLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
      toast.error('Fout bij ophalen van gegevens');
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
    toast.success('Uitgelogd');
  };

  // Event handlers
  const saveEvent = async (eventData) => {
    try {
      if (eventDialog.data) {
        await axios.put(`${API}/events/${eventDialog.data.id}`, eventData, getAuthHeaders());
        toast.success('Evenement bijgewerkt');
      } else {
        await axios.post(`${API}/events`, eventData, getAuthHeaders());
        toast.success('Evenement toegevoegd');
      }
      fetchData();
      setEventDialog({ open: false, data: null });
    } catch (error) {
      toast.error('Fout bij opslaan evenement');
    }
  };

  const deleteEvent = async (id) => {
    setDeleteDialog({ open: true, type: 'event', id });
  };

  // Team handlers
  const saveTeamMember = async (memberData) => {
    try {
      if (teamDialog.data) {
        await axios.put(`${API}/team/${teamDialog.data.id}`, memberData, getAuthHeaders());
        toast.success('Teamlid bijgewerkt');
      } else {
        await axios.post(`${API}/team`, memberData, getAuthHeaders());
        toast.success('Teamlid toegevoegd');
      }
      fetchData();
      setTeamDialog({ open: false, data: null });
    } catch (error) {
      toast.error('Fout bij opslaan teamlid');
    }
  };

  const deleteTeamMember = async (id) => {
    setDeleteDialog({ open: true, type: 'team', id });
  };
  
  const confirmDelete = async () => {
    const { type, id } = deleteDialog;
    try {
      if (type === 'team') {
        await axios.delete(`${API}/team/${id}`, getAuthHeaders());
        toast.success('Teamlid verwijderd');
      } else if (type === 'event') {
        await axios.delete(`${API}/events/${id}`, getAuthHeaders());
        toast.success('Evenement verwijderd');
      } else if (type === 'partner') {
        await axios.delete(`${API}/partners/${id}`, getAuthHeaders());
        toast.success('Partner verwijderd');
      } else if (type === 'album') {
        await axios.delete(`${API}/albums/${id}`, getAuthHeaders());
        toast.success('Album verwijderd');
      } else if (type === 'hero-gallery') {
        await axios.delete(`${API}/hero-gallery/${id}`, getAuthHeaders());
        toast.success('Hero galerij item verwijderd');
      }
      fetchData();
      setDeleteDialog({ open: false, type: null, id: null });
    } catch (error) {
      toast.error('Fout bij verwijderen');
      setDeleteDialog({ open: false, type: null, id: null });
    }
  };

  // Settings handler
  const saveSettings = async (settingsData) => {
    try {
      await axios.put(`${API}/settings`, settingsData, getAuthHeaders());
      toast.success('Instellingen opgeslagen');
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan instellingen');
    }
  };

  // Partner handlers
  const savePartner = async (partnerData) => {
    try {
      if (partnerDialog.data) {
        await axios.put(`${API}/partners/${partnerDialog.data.id}`, partnerData, getAuthHeaders());
        toast.success('Partner bijgewerkt');
      } else {
        await axios.post(`${API}/partners`, partnerData, getAuthHeaders());
        toast.success('Partner toegevoegd');
      }
      fetchData();
      setPartnerDialog({ open: false, data: null });
    } catch (error) {
      toast.error('Fout bij opslaan partner');
    }
  };

  const deletePartner = async (id) => {
    setDeleteDialog({ open: true, type: 'partner', id });
  };

  // Album handlers
  const saveAlbum = async (albumData) => {
    try {
      if (albumDialog.data) {
        await axios.put(`${API}/albums/${albumDialog.data.id}`, albumData, getAuthHeaders());
        toast.success('Album bijgewerkt');
      } else {
        await axios.post(`${API}/albums`, albumData, getAuthHeaders());
        toast.success('Album toegevoegd');
      }
      fetchData();
      setAlbumDialog({ open: false, data: null });
    } catch (error) {
      toast.error('Fout bij opslaan album');
    }
  };

  const deleteAlbum = async (id) => {
    setDeleteDialog({ open: true, type: 'album', id });
  };

  const viewAlbumPhotos = async (albumId) => {
    try {
      const response = await axios.get(`${API}/albums/${albumId}/photos`);
      setSelectedAlbumPhotos(response.data);
      setPhotoDialog({ open: true, albumId });
    } catch (error) {
      toast.error('Fout bij ophalen foto\'s');
    }
  };

  const savePhoto = async (photoData) => {
    try {
      await axios.post(`${API}/photos`, photoData, getAuthHeaders());
      toast.success('Foto toegevoegd');
      // Refresh photos for current album
      const response = await axios.get(`${API}/albums/${photoData.album_id}/photos`);
      setSelectedAlbumPhotos(response.data);
    } catch (error) {
      toast.error('Fout bij opslaan foto');
    }
  };

  const deletePhoto = async (photoId, albumId) => {
    if (!window.confirm('Weet je zeker dat je deze foto wilt verwijderen?')) return;
    try {
      await axios.delete(`${API}/photos/${photoId}`, getAuthHeaders());
      toast.success('Foto verwijderd');
      // Refresh photos
      const response = await axios.get(`${API}/albums/${albumId}/photos`);
      setSelectedAlbumPhotos(response.data);
    } catch (error) {
      toast.error('Fout bij verwijderen foto');
    }
  };

  // Hero Gallery handlers
  const saveHeroGalleryItem = async (itemData) => {
    try {
      if (heroGalleryDialog.data) {
        await axios.put(`${API}/hero-gallery/${heroGalleryDialog.data.id}`, itemData, getAuthHeaders());
        toast.success('Hero galerij item bijgewerkt');
      } else {
        await axios.post(`${API}/hero-gallery`, itemData, getAuthHeaders());
        toast.success('Hero galerij item toegevoegd');
      }
      fetchData();
      setHeroGalleryDialog({ open: false, data: null });
    } catch (error) {
      toast.error('Fout bij opslaan hero galerij item');
    }
  };

  const deleteHeroGalleryItem = async (id) => {
    setDeleteDialog({ open: true, type: 'hero-gallery', id });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="dashboard-title">Admin Dashboard</h1>
          <div className="flex gap-4">
            <Button 
              variant="outline" 
              onClick={async () => {
                try {
                  const token = localStorage.getItem('adminToken');
                  const response = await axios.get(`${API}/admin/export-database`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  
                  // Download as JSON file
                  const dataStr = JSON.stringify(response.data, null, 2);
                  const blob = new Blob([dataStr], { type: 'application/json' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `database-backup-${new Date().toISOString().split('T')[0]}.json`;
                  a.click();
                  toast.success('Database backup succesvol gedownload!');
                } catch (error) {
                  toast.error('Fout bij maken backup');
                }
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="backup-button"
            >
              💾 Backup Database
            </Button>
            <Button variant="outline" onClick={() => window.open('/', '_blank')} data-testid="view-site-button">
              Bekijk Website
            </Button>
            <Button variant="destructive" onClick={handleLogout} data-testid="logout-button">
              Uitloggen
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="events" className="w-full" data-testid="admin-tabs">
          {/* Tabs in 2 rijen voor betere overzichtelijkheid */}
          <div className="space-y-3 mb-8">
            {/* Eerste rij - Content beheer */}
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-xs font-semibold text-gray-500 uppercase px-2 mb-2">Website Content</p>
              <TabsList className="grid w-full grid-cols-5 gap-2 bg-transparent">
                <TabsTrigger value="events" data-testid="tab-events" className="data-[state=active]:bg-white">Evenementen</TabsTrigger>
                <TabsTrigger value="team" data-testid="tab-team" className="data-[state=active]:bg-white">Team</TabsTrigger>
                <TabsTrigger value="albums" data-testid="tab-albums" className="data-[state=active]:bg-white">Albums</TabsTrigger>
                <TabsTrigger value="partners" data-testid="tab-partners" className="data-[state=active]:bg-white">Partners</TabsTrigger>
                <TabsTrigger value="hero-gallery" data-testid="tab-hero-gallery" className="data-[state=active]:bg-white">Hero Galerij</TabsTrigger>
              </TabsList>
            </div>
            
            {/* Tweede rij - Registraties & Instellingen */}
            <div className="bg-blue-50 rounded-lg p-2">
              <p className="text-xs font-semibold text-gray-500 uppercase px-2 mb-2">Registraties & Beheer</p>
              <TabsList className="grid w-full grid-cols-6 gap-2 bg-transparent">
                <TabsTrigger value="bibliotheek" data-testid="tab-bibliotheek" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white font-semibold">
                  🗂️ Bibliotheek
                </TabsTrigger>
                <TabsTrigger value="nieuwjaarsreceptie" data-testid="tab-nieuwjaars" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white font-semibold">Nieuwjaarsreceptie</TabsTrigger>
                <TabsTrigger value="newsletter" data-testid="tab-newsletter" className="data-[state=active]:bg-white">Nieuwsbrief</TabsTrigger>
                <TabsTrigger value="volunteers" data-testid="tab-volunteers" className="data-[state=active]:bg-white">Vrijwilligers</TabsTrigger>
                <TabsTrigger value="messages" data-testid="tab-messages" className="data-[state=active]:bg-white">Berichten</TabsTrigger>
                <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-white">Instellingen</TabsTrigger>
              </TabsList>
            </div>
          </div>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card>
              <CardHeader>
                <div className="flex flex-row justify-between items-center">
                  <CardTitle>Evenementen Beheren</CardTitle>
                  <Button 
                    onClick={() => setEventDialog({ open: true, data: null })} 
                    className="bg-[#0a56c2] hover:bg-[#083d8a]"
                    data-testid="add-event-button"
                  >
                    + Nieuw Evenement
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4" data-testid="events-list">
                  {events.map((event, index) => (
                    <div key={event.id} className="border rounded-lg p-4 flex justify-between items-start" data-testid={`event-item-${index}`}>
                      <div>
                        <h3 className="font-bold text-lg">{event.title}</h3>
                        <p className="text-sm text-gray-600">{event.date} • {event.time}</p>
                        <p className="text-sm text-gray-700 mt-2">{event.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setEventDialog({ open: true, data: event })}
                          data-testid={`edit-event-${index}`}
                        >
                          Bewerken
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => deleteEvent(event.id)}
                          data-testid={`delete-event-${index}`}
                        >
                          Verwijderen
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team">
            <Card>
              <CardHeader>
                <div className="flex flex-row justify-between items-center">
                  <CardTitle>Team Beheren</CardTitle>
                  <Button 
                    onClick={() => setTeamDialog({ open: true, data: null })} 
                    className="bg-[#0a56c2] hover:bg-[#083d8a]"
                    data-testid="add-team-button"
                  >
                    + Nieuw Teamlid
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-list">
                  {team.map((member, index) => (
                    <div key={member.id} className="border rounded-lg p-4" data-testid={`team-item-${index}`}>
                      {member.image_url && (
                        <img src={getImageUrl(member.image_url)} alt={member.first_name} className="w-full h-48 object-cover rounded-lg mb-3" />
                      )}
                      <h3 className="font-bold">{member.first_name} {member.last_name}</h3>
                      <p className="text-sm text-gray-600">{member.role}</p>
                      <div className="flex gap-2 mt-3">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setTeamDialog({ open: true, data: member })}
                          data-testid={`edit-team-${index}`}
                        >
                          Bewerken
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => deleteTeamMember(member.id)}
                          data-testid={`delete-team-${index}`}
                        >
                          Verwijderen
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Albums Tab */}
          <TabsContent value="albums">
            <Card>
              <CardHeader>
                <div className="flex flex-row justify-between items-center">
                  <CardTitle>Foto Albums Beheren</CardTitle>
                  <Button 
                    onClick={() => setAlbumDialog({ open: true, data: null })} 
                    className="bg-[#0a56c2] hover:bg-[#083d8a]"
                    data-testid="add-album-button"
                  >
                    + Nieuw Album
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4" data-testid="albums-list">
                  {albums.map((album, index) => (
                    <div key={album.id} className="border rounded-lg p-4" data-testid={`album-item-${index}`}>
                      <div className="flex gap-4">
                        {album.cover_image_url && (
                          <div className="w-32 h-32 flex-shrink-0">
                            <img src={getImageUrl(album.cover_image_url)} alt={album.title} className="w-full h-full object-cover rounded-lg" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-bold text-lg">{album.title}</h3>
                          <p className="text-sm text-gray-600">{album.date}</p>
                          <p className="text-sm text-gray-700 mt-2">{album.description}</p>
                          <div className="flex gap-2 mt-3">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => viewAlbumPhotos(album.id)}
                              data-testid={`view-photos-${index}`}
                            >
                              📷 Foto's ({selectedAlbumPhotos.filter(p => p.album_id === album.id).length})
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => setAlbumDialog({ open: true, data: album })}
                              data-testid={`edit-album-${index}`}
                            >
                              Bewerken
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm" 
                              onClick={() => deleteAlbum(album.id)}
                              data-testid={`delete-album-${index}`}
                            >
                              Verwijderen
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {albums.length === 0 && (
                    <p className="text-center text-gray-500" data-testid="no-albums">Nog geen albums toegevoegd</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <Card>
              <CardHeader>
                <div className="flex flex-row justify-between items-center">
                  <CardTitle>Partners Beheren</CardTitle>
                  <Button 
                    onClick={() => setPartnerDialog({ open: true, data: null })} 
                    className="bg-[#0a56c2] hover:bg-[#083d8a]"
                    data-testid="add-partner-button"
                  >
                    + Nieuwe Partner
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="partners-list">
                  {partners.map((partner, index) => (
                    <div key={partner.id} className="border rounded-lg p-4" data-testid={`partner-item-${index}`}>
                      <div className="flex items-center justify-center bg-gray-50 rounded-lg p-4 mb-3 h-24">
                        <img src={getImageUrl(partner.logo_url)} alt={partner.name} className="max-h-16 w-auto object-contain" />
                      </div>
                      <h3 className="font-bold text-center mb-1">{partner.name}</h3>
                      <p className="text-sm text-gray-600 text-center mb-3 truncate">{partner.website_url}</p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setPartnerDialog({ open: true, data: partner })}
                          className="flex-1"
                          data-testid={`edit-partner-${index}`}
                        >
                          Bewerken
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => deletePartner(partner.id)}
                          className="flex-1"
                          data-testid={`delete-partner-${index}`}
                        >
                          Verwijderen
                        </Button>
                      </div>
                    </div>
                  ))}
                  {partners.length === 0 && (
                    <p className="col-span-3 text-center text-gray-500" data-testid="no-partners">Nog geen partners toegevoegd</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Site Instellingen</CardTitle>
              </CardHeader>
              <CardContent>
                <SettingsForm settings={settings} onSave={saveSettings} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hero Gallery Tab */}
          <TabsContent value="hero-gallery">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-row justify-between items-center">
                    <CardTitle>Hero Galerij Beheren</CardTitle>
                    <Button 
                      onClick={() => setHeroGalleryDialog({ open: true, data: null })} 
                      className="bg-[#0a56c2] hover:bg-[#083d8a]"
                      data-testid="add-hero-gallery-button"
                    >
                      + Nieuw Item
                    </Button>
                  </div>
                  {/* Fix Database URLs Button */}
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Na eerste deployment:</strong> Klik deze knop EENMALIG om oude afbeelding URLs te converteren naar Cloudinary URLs.
                    </p>
                    <Button 
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('adminToken');
                          const response = await axios.post(
                            `${API}/admin/fix-database-urls`,
                            {},
                            { headers: { 'Authorization': `Bearer ${token}` }}
                          );
                          toast.success(`Succesvol! ${response.data.total_fixed} URLs geconverteerd naar Cloudinary`);
                          fetchData(); // Refresh data
                        } catch (error) {
                          toast.error('Fout bij URL conversie');
                        }
                      }}
                      className="bg-yellow-500 hover:bg-yellow-600 text-white"
                    >
                      🔧 Fix Database URLs naar Cloudinary
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4" data-testid="hero-gallery-list">
                  {heroGallery.map((item, index) => (
                    <div key={item.id} className="border rounded-lg p-4 flex justify-between items-start" data-testid={`hero-gallery-item-${index}`}>
                      <div className="flex gap-4">
                        {item.image_url && (
                          <img 
                            src={getImageUrl(item.image_url)} 
                            alt={item.title}
                            className="w-24 h-24 object-cover rounded"
                          />
                        )}
                        <div>
                          <h3 className="font-bold text-lg">{item.title}</h3>
                          <p className="text-sm text-gray-700 mt-2">{item.description}</p>
                          <p className="text-xs text-gray-500 mt-1">Volgorde: {item.order}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setHeroGalleryDialog({ open: true, data: item })}
                          data-testid={`edit-hero-gallery-${index}`}
                        >
                          Bewerken
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => deleteHeroGalleryItem(item.id)}
                          data-testid={`delete-hero-gallery-${index}`}
                        >
                          Verwijderen
                        </Button>
                      </div>
                    </div>
                  ))}
                  {heroGallery.length === 0 && (
                    <p className="text-center text-gray-500" data-testid="no-hero-gallery">Geen hero galerij items</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Newsletter Tab */}
          <TabsContent value="newsletter">
            <Card>
              <CardHeader>
                <CardTitle>Nieuwsbrief Inschrijvingen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="font-semibold text-blue-900">Totaal inschrijvingen: {newsletterSubscribers.length}</h4>
                  </div>
                  <p className="text-sm text-blue-800 ml-9">
                    Exporteer deze lijst naar een CSV bestand om nieuwsbrieven te versturen via je e-mail provider
                  </p>
                </div>
                
                <div className="space-y-3" data-testid="newsletter-list">
                  {newsletterSubscribers.map((subscriber, index) => (
                    <div 
                      key={subscriber.id} 
                      className="flex justify-between items-center border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      data-testid={`subscriber-item-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#0a56c2] rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{subscriber.email}</p>
                          <p className="text-sm text-gray-500">
                            Ingeschreven op {new Date(subscriber.subscribed_at).toLocaleDateString('nl-BE', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            data-testid={`delete-subscriber-${index}`}
                          >
                            Verwijderen
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Inschrijving verwijderen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Weet je zeker dat je {subscriber.email} wilt verwijderen van de nieuwsbrief? 
                              Deze actie kan niet ongedaan gemaakt worden.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuleren</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={async () => {
                                try {
                                  await axios.delete(
                                    `${API}/newsletter/subscribers/${subscriber.id}`,
                                    getAuthHeaders()
                                  );
                                  toast.success('Inschrijving verwijderd');
                                  fetchData();
                                } catch (error) {
                                  toast.error('Fout bij verwijderen inschrijving');
                                }
                              }}
                            >
                              Verwijderen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ))}
                  {newsletterSubscribers.length === 0 && (
                    <div className="text-center py-12" data-testid="no-subscribers">
                      <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-500 font-medium">Nog geen nieuwsbrief inschrijvingen</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Bezoekers kunnen zich inschrijven via de gele sectie op de homepage
                      </p>
                    </div>
                  )}
                </div>
                
                {newsletterSubscribers.length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <Button
                      onClick={() => {
                        const csv = newsletterSubscribers.map(s => s.email).join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `nieuwsbrief-${new Date().toISOString().split('T')[0]}.csv`;
                        a.click();
                        toast.success('E-mailadressen geëxporteerd naar CSV');
                      }}
                      className="w-full"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Exporteer als CSV
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Volunteers Tab */}
          <TabsContent value="volunteers">
            <Card>
              <CardHeader>
                <CardTitle>Vrijwilligers - Vrienden van het Feestcomité</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h4 className="font-semibold text-green-900">Totaal vrijwilligers: {volunteers.length}</h4>
                  </div>
                  <p className="text-sm text-green-800 ml-9">
                    Beheer hier alle vrijwilligers die zich hebben aangemeld via de "Vrienden van het Feestcomité" pagina
                  </p>
                </div>
                
                <div className="space-y-4" data-testid="volunteers-list">
                  {volunteers.map((volunteer, index) => (
                    <div 
                      key={volunteer.id} 
                      className="border rounded-lg p-6 hover:bg-gray-50 transition-colors"
                      data-testid={`volunteer-item-${index}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="text-lg font-bold text-gray-900">
                            {volunteer.first_name} {volunteer.last_name}
                          </h4>
                          <p className="text-sm text-gray-500">
                            Aangemeld op {new Date(volunteer.created_at).toLocaleDateString('nl-BE', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              data-testid={`delete-volunteer-${index}`}
                            >
                              Verwijderen
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Vrijwilliger verwijderen?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Weet je zeker dat je {volunteer.first_name} {volunteer.last_name} wilt verwijderen? 
                                Deze actie kan niet ongedaan gemaakt worden.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuleren</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.delete(
                                      `${API}/volunteers/${volunteer.id}`,
                                      getAuthHeaders()
                                    );
                                    toast.success('Vrijwilliger verwijderd');
                                    fetchData();
                                  } catch (error) {
                                    toast.error('Fout bij verwijderen vrijwilliger');
                                  }
                                }}
                              >
                                Verwijderen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 mb-1">
                            <span className="font-semibold">Email:</span> {volunteer.email}
                          </p>
                          {volunteer.phone && (
                            <p className="text-gray-600 mb-1">
                              <span className="font-semibold">Telefoon:</span> {volunteer.phone}
                            </p>
                          )}
                          {volunteer.street && (
                            <p className="text-gray-600 mb-1">
                              <span className="font-semibold">Adres:</span> {volunteer.street}, {volunteer.postal_code} {volunteer.city}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-gray-600 mb-1">
                            <span className="font-semibold">Beschikbaarheid:</span> {volunteer.availability}
                          </p>
                          {volunteer.help_with && volunteer.help_with.length > 0 && (
                            <div className="mb-2">
                              <p className="font-semibold text-gray-700 mb-1">Wil helpen met:</p>
                              <ul className="list-disc list-inside text-gray-600 text-xs">
                                {volunteer.help_with.map((item, idx) => (
                                  <li key={idx}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>

                      {volunteer.comments && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="font-semibold text-gray-700 mb-1">Opmerkingen:</p>
                          <p className="text-sm text-gray-600 italic">"{volunteer.comments}"</p>
                        </div>
                      )}
                    </div>
                  ))}
                  {volunteers.length === 0 && (
                    <div className="text-center py-12" data-testid="no-volunteers">
                      <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p className="text-gray-500 font-medium">Nog geen vrijwilligers aangemeld</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Bezoekers kunnen zich aanmelden via de pagina /vrienden
                      </p>
                    </div>
                  )}
                </div>

                {volunteers.length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <Button
                      onClick={() => {
                        const csv = volunteers.map(v => 
                          `"${v.first_name}","${v.last_name}","${v.email}","${v.phone || ''}","${v.street || ''}","${v.postal_code || ''}","${v.city || ''}","${v.availability}","${v.help_with.join('; ')}","${v.comments || ''}"`
                        ).join('\n');
                        const header = '"Voornaam","Naam","Email","Telefoon","Straat","Postcode","Gemeente","Beschikbaarheid","Helpen met","Opmerkingen"\n';
                        const blob = new Blob([header + csv], { type: 'text/csv;charset=utf-8;' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `vrijwilligers-${new Date().toISOString().split('T')[0]}.csv`;
                        a.click();
                        toast.success('Vrijwilligers geëxporteerd naar CSV');
                      }}
                      className="w-full"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Exporteer alle vrijwilligers als CSV
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle>Contact Berichten</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4" data-testid="messages-list">
                  {messages.map((msg, index) => (
                    <div key={msg.id} className="border rounded-lg p-4" data-testid={`message-item-${index}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-bold">{msg.name}</h4>
                          <p className="text-sm text-gray-600">{msg.email}</p>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(msg.created_at).toLocaleDateString('nl-BE')}
                        </span>
                      </div>
                      <p className="text-gray-700">{msg.message}</p>
                    </div>
                  ))}
                  {messages.length === 0 && (
                    <p className="text-center text-gray-500" data-testid="no-messages">Geen berichten ontvangen</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>


            {/* Nieuwjaarsreceptie Tab */}
            <TabsContent value="nieuwjaarsreceptie">
              <NieuwjaarsreceptieTab />
            </TabsContent>

            {/* Bibliotheek Tab */}
            <TabsContent value="bibliotheek">
              <BibliotheekTab />
            </TabsContent>

        </Tabs>
      </main>

      {/* Event Dialog */}
      <EventDialog 
        open={eventDialog.open}
        data={eventDialog.data}
        onClose={() => setEventDialog({ open: false, data: null })}
        onSave={saveEvent}
      />

      {/* Team Dialog */}
      <TeamDialog 
        open={teamDialog.open}
        data={teamDialog.data}
        onClose={() => setTeamDialog({ open: false, data: null })}
        onSave={saveTeamMember}
      />

      {/* Partner Dialog */}
      <PartnerDialog 
        open={partnerDialog.open}
        data={partnerDialog.data}
        onClose={() => setPartnerDialog({ open: false, data: null })}
        onSave={savePartner}
      />

      {/* Album Dialog */}
      <AlbumDialog 
        open={albumDialog.open}
        data={albumDialog.data}
        onClose={() => setAlbumDialog({ open: false, data: null })}
        onSave={saveAlbum}
      />

      {/* Photo Management Dialog */}
      <PhotoDialog 
        open={photoDialog.open}
        albumId={photoDialog.albumId}
        photos={selectedAlbumPhotos}
        onClose={() => {
          setPhotoDialog({ open: false, albumId: null });
          setSelectedAlbumPhotos([]);
        }}
        onSave={savePhoto}
        onDelete={deletePhoto}
      />

      {/* Hero Gallery Dialog */}
      <HeroGalleryDialog 
        open={heroGalleryDialog.open}
        data={heroGalleryDialog.data}
        onClose={() => setHeroGalleryDialog({ open: false, data: null })}
        onSave={saveHeroGalleryItem}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Weet je het zeker?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze actie kan niet ongedaan gemaakt worden. Dit zal het item permanent verwijderen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Event Dialog Component
const EventDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    description: '',
    image_url: '',
    order: 0
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (data) {
      setFormData(data);
    } else {
      setFormData({
        title: '',
        date: '',
        time: '',
        description: '',
        image_url: '',
        order: 0
      });
    }
    setImageFile(null);
  }, [data, open]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/upload`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, image_url: response.data.url });
      toast.success('Afbeelding geüpload');
    } catch (error) {
      toast.error('Fout bij uploaden afbeelding');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle>{data ? 'Evenement Bewerken' : 'Nieuw Evenement'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="event-form">
          <div>
            <label className="block text-sm font-medium mb-2">Titel</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              data-testid="event-title-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Datum</label>
              <Input
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                placeholder="bijv. ZATERDAG 15 AUG 2026"
                data-testid="event-date-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Tijd</label>
              <Input
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                required
                placeholder="bijv. 14:00"
                data-testid="event-time-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Beschrijving</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={3}
              data-testid="event-description-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Afbeelding</label>
            <Input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploadingImage}
              data-testid="event-image-input"
            />
            {uploadingImage && <p className="text-sm text-gray-500 mt-1">Uploaden...</p>}
            {formData.image_url && !uploadingImage && (
              <div className="mt-2 relative">
                <img src={getImageUrl(formData.image_url)} alt="Preview" className="h-32 w-auto rounded-lg" />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-1 right-1"
                  onClick={() => setFormData({ ...formData, image_url: '' })}
                >
                  Verwijderen
                </Button>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Volgorde</label>
            <Input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              data-testid="event-order-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="event-cancel-button">
              Annuleren
            </Button>
            <Button type="submit" className="bg-[#0a56c2] hover:bg-[#083d8a]" data-testid="event-save-button">
              Opslaan
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Team Dialog Component
const TeamDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    role: '',
    image_url: '',
    order: 0
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData(data);
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        role: '',
        image_url: '',
        order: 0
      });
    }
  }, [data, open]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/upload`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, image_url: response.data.url });
      toast.success('Afbeelding geüpload');
    } catch (error) {
      toast.error('Fout bij uploaden afbeelding');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="team-dialog">
        <DialogHeader>
          <DialogTitle>{data ? 'Teamlid Bewerken' : 'Nieuw Teamlid'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="team-form">
          <div>
            <label className="block text-sm font-medium mb-2">Voornaam</label>
            <Input
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
              data-testid="team-firstname-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Achternaam</label>
            <Input
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              required
              data-testid="team-lastname-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Rol</label>
            <Input
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
              placeholder="bijv. Voorzitter, Bestuur"
              data-testid="team-role-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Afbeelding</label>
            <Input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploadingImage}
              data-testid="team-image-input"
            />
            {uploadingImage && <p className="text-sm text-gray-500 mt-1">Uploaden...</p>}
            {formData.image_url && !uploadingImage && (
              <div className="mt-2 relative">
                <img src={getImageUrl(formData.image_url)} alt="Preview" className="h-32 w-auto rounded-lg" />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-1 right-1"
                  onClick={() => setFormData({ ...formData, image_url: '' })}
                >
                  Verwijderen
                </Button>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Volgorde</label>
            <Input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              data-testid="team-order-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="team-cancel-button">
              Annuleren
            </Button>
            <Button type="submit" className="bg-[#0a56c2] hover:bg-[#083d8a]" data-testid="team-save-button">
              Opslaan
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Settings Form Component
const SettingsForm = ({ settings, onSave }) => {
  const [formData, setFormData] = useState({
    countdown_date: '',
    countdown_title: '',
    countdown_subtitle: '',
    about_text: '',
    logo_url: ''
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/upload`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, logo_url: response.data.url });
      toast.success('Logo geüpload');
    } catch (error) {
      toast.error('Fout bij uploaden logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="settings-form">
      <div>
        <label className="block text-sm font-medium mb-2">Afteldatum (ISO formaat)</label>
        <Input
          value={formData.countdown_date}
          onChange={(e) => setFormData({ ...formData, countdown_date: e.target.value })}
          placeholder="2026-08-15T14:00:00+02:00"
          required
          data-testid="settings-countdown-date-input"
        />
        <p className="text-xs text-gray-500 mt-1">Formaat: YYYY-MM-DDTHH:MM:SS+02:00</p>
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Aftel Titel</label>
        <Input
          value={formData.countdown_title}
          onChange={(e) => setFormData({ ...formData, countdown_title: e.target.value })}
          data-testid="settings-countdown-title-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Aftel Ondertitel</label>
        <Input
          value={formData.countdown_subtitle}
          onChange={(e) => setFormData({ ...formData, countdown_subtitle: e.target.value })}
          data-testid="settings-countdown-subtitle-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Over Ons Tekst</label>
        <Textarea
          value={formData.about_text}
          onChange={(e) => setFormData({ ...formData, about_text: e.target.value })}
          rows={5}
          required
          data-testid="settings-about-text-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Logo</label>
        <Input
          type="file"
          accept="image/*"
          onChange={handleLogoUpload}
          disabled={uploadingLogo}
          data-testid="settings-logo-url-input"
        />
        {uploadingLogo && <p className="text-sm text-gray-500 mt-1">Uploaden...</p>}
        {formData.logo_url && !uploadingLogo && (
          <div className="mt-2 relative inline-block">
            <img src={getImageUrl(formData.logo_url)} alt="Logo Preview" className="h-20 w-auto rounded-lg" />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute top-1 right-1"
              onClick={() => setFormData({ ...formData, logo_url: '' })}
            >
              Verwijderen
            </Button>
          </div>
        )}
      </div>
      <Button type="submit" className="bg-[#0a56c2] hover:bg-[#083d8a]" data-testid="settings-save-button">
        Instellingen Opslaan
      </Button>
    </form>
  );
};

// Album Dialog Component
const AlbumDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    cover_image_url: '',
    order: 0
  });
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData(data);
    } else {
      setFormData({
        title: '',
        description: '',
        date: '',
        cover_image_url: '',
        order: 0
      });
    }
  }, [data, open]);

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingCover(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/upload`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, cover_image_url: response.data.url });
      toast.success('Cover foto geüpload');
    } catch (error) {
      toast.error('Fout bij uploaden cover foto');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="album-dialog">
        <DialogHeader>
          <DialogTitle>{data ? 'Album Bewerken' : 'Nieuw Album'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="album-form">
          <div>
            <label className="block text-sm font-medium mb-2">Titel</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="bijv. Kermis Leupegem 2024"
              data-testid="album-title-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Datum</label>
            <Input
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              placeholder="bijv. Augustus 2024"
              data-testid="album-date-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Beschrijving</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={3}
              data-testid="album-description-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Cover Foto</label>
            <Input
              type="file"
              accept="image/*"
              onChange={handleCoverUpload}
              disabled={uploadingCover}
              data-testid="album-cover-input"
            />
            {uploadingCover && <p className="text-sm text-gray-500 mt-1">Uploaden...</p>}
            {formData.cover_image_url && !uploadingCover && (
              <div className="mt-2 relative">
                <img src={getImageUrl(formData.cover_image_url)} alt="Cover Preview" className="h-32 w-auto rounded-lg" />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-1 right-1"
                  onClick={() => setFormData({ ...formData, cover_image_url: '' })}
                >
                  Verwijderen
                </Button>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Volgorde</label>
            <Input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              data-testid="album-order-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="album-cancel-button">
              Annuleren
            </Button>
            <Button type="submit" className="bg-[#0a56c2] hover:bg-[#083d8a]" data-testid="album-save-button">
              Opslaan
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Photo Dialog Component
const PhotoDialog = ({ open, albumId, photos, onClose, onSave, onDelete }) => {
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoCaption, setPhotoCaption] = useState('');

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/upload`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Save photo to album
      await onSave({
        album_id: albumId,
        image_url: response.data.url,
        caption: photoCaption,
        order: photos.length
      });
      
      setPhotoCaption('');
      toast.success('Foto toegevoegd');
      e.target.value = ''; // Reset file input
    } catch (error) {
      toast.error('Fout bij uploaden foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleBulkPhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhoto(true);
    
    try {
      const token = localStorage.getItem('adminToken');
      let successCount = 0;
      let errorCount = 0;
      
      toast.info(`Uploaden van ${files.length} foto's gestart...`);
      
      // Upload files one by one with progress
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        try {
          // Upload to Cloudinary
          const response = await axios.post(`${BACKEND_URL}/api/upload`, formDataUpload, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          // Save photo to album
          await onSave({
            album_id: albumId,
            image_url: response.data.url,
            caption: `Foto ${i + 1}`,
            order: photos.length + i
          });
          
          successCount++;
          
          // Update progress
          if ((i + 1) % 5 === 0 || i === files.length - 1) {
            toast.info(`${successCount}/${files.length} foto's geüpload...`);
          }
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          errorCount++;
        }
      }
      
      // Show final result
      if (successCount > 0) {
        toast.success(`${successCount} foto's succesvol toegevoegd!`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} foto's konden niet worden geüpload`);
      }
      
      e.target.value = ''; // Reset file input
    } catch (error) {
      toast.error('Fout bij bulk upload');
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="photo-dialog">
        <DialogHeader>
          <DialogTitle>Foto's Beheren</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* Upload Section */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
            <h3 className="font-semibold mb-4">Foto's Toevoegen</h3>
            <div className="space-y-4">
              {/* Single Upload */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Enkele foto met bijschrift
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">Bijschrift (optioneel)</label>
                    <Input
                      value={photoCaption}
                      onChange={(e) => setPhotoCaption(e.target.value)}
                      placeholder="Beschrijving van de foto"
                      data-testid="photo-caption-input"
                    />
                  </div>
                  <div>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto}
                      data-testid="photo-upload-input"
                    />
                  </div>
                </div>
              </div>

              {/* Bulk Upload */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Meerdere foto's tegelijk (Bulk Upload)
                </h4>
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Selecteer meerdere foto's om ze allemaal tegelijk te uploaden
                  </p>
                  <div>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleBulkPhotoUpload}
                      disabled={uploadingPhoto}
                      data-testid="bulk-photo-upload-input"
                    />
                  </div>
                </div>
              </div>

              {uploadingPhoto && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <svg className="animate-spin h-5 w-5 text-yellow-600" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <p className="text-sm font-medium text-yellow-800">Bezig met uploaden... Even geduld</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Photos Grid */}
          <div>
            <h3 className="font-semibold mb-4">Album Foto's ({photos.length})</h3>
            {photos.length > 0 ? (
              <div className="grid grid-cols-3 gap-4">
                {photos.map((photo, index) => (
                  <div key={photo.id} className="relative group" data-testid={`photo-item-${index}`}>
                    <img 
                      src={photo.image_url} 
                      alt={photo.caption || `Foto ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    {photo.caption && (
                      <p className="text-xs text-gray-600 mt-1 truncate">{photo.caption}</p>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => onDelete(photo.id, albumId)}
                      data-testid={`delete-photo-${index}`}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nog geen foto's in dit album</p>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={onClose} data-testid="photo-close-button">
              Sluiten
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Partner Dialog Component
const PartnerDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    website_url: '',
    order: 0
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData(data);
    } else {
      setFormData({
        name: '',
        logo_url: '',
        website_url: '',
        order: 0
      });
    }
  }, [data, open]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/upload`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, logo_url: response.data.url });
      toast.success('Logo geüpload');
    } catch (error) {
      toast.error('Fout bij uploaden logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="partner-dialog">
        <DialogHeader>
          <DialogTitle>{data ? 'Partner Bewerken' : 'Nieuwe Partner'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="partner-form">
          <div>
            <label className="block text-sm font-medium mb-2">Naam</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Bedrijfsnaam"
              data-testid="partner-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Logo</label>
            <Input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              disabled={uploadingLogo}
              required={!formData.logo_url}
              data-testid="partner-logo-input"
            />
            {uploadingLogo && <p className="text-sm text-gray-500 mt-1">Uploaden...</p>}
            {formData.logo_url && !uploadingLogo && (
              <div className="mt-2 p-4 bg-gray-50 rounded-lg flex items-center justify-center relative">
                <img src={getImageUrl(formData.logo_url)} alt="Preview" className="max-h-20 object-contain" />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-1 right-1"
                  onClick={() => setFormData({ ...formData, logo_url: '' })}
                >
                  Verwijderen
                </Button>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Website URL</label>
            <Input
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              required
              placeholder="https://www.voorbeeld.be"
              data-testid="partner-website-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Volgorde</label>
            <Input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              data-testid="partner-order-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="partner-cancel-button">
              Annuleren
            </Button>
            <Button type="submit" className="bg-[#0a56c2] hover:bg-[#083d8a]" data-testid="partner-save-button">
              Opslaan
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Hero Gallery Dialog Component
const HeroGalleryDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    order: 0
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData(data);
    } else {
      setFormData({
        title: '',
        description: '',
        image_url: '',
        order: 0
      });
    }
  }, [data, open]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/upload`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, image_url: response.data.url });
      toast.success('Afbeelding geüpload');
    } catch (error) {
      toast.error('Fout bij uploaden afbeelding');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="hero-gallery-dialog">
        <DialogHeader>
          <DialogTitle>{data ? 'Hero Item Bewerken' : 'Nieuw Hero Item'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="hero-gallery-form">
          <div>
            <label className="block text-sm font-medium mb-2">Titel</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="Titel"
              data-testid="hero-title-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Beschrijving</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              placeholder="Beschrijving"
              rows={3}
              data-testid="hero-description-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Afbeelding</label>
            <Input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploadingImage}
              required={!formData.image_url}
              data-testid="hero-image-input"
            />
            {uploadingImage && <p className="text-sm text-gray-500 mt-1">Uploaden...</p>}
            {formData.image_url && !uploadingImage && (
              <div className="mt-2">
                <img src={getImageUrl(formData.image_url)} alt="Preview" className="w-32 h-32 object-cover rounded" />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Volgorde</label>
            <Input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              data-testid="hero-order-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="hero-cancel-button">
              Annuleren
            </Button>
            <Button type="submit" className="bg-[#0a56c2] hover:bg-[#083d8a]" data-testid="hero-save-button">
              Opslaan
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDashboard;